import {Combobox, ComboboxInput, ComboboxOption, ComboboxOptions} from '@headlessui/react';
import clsx from 'clsx';
import {debounce} from 'lodash';
import React, {ChangeEvent, FC, useCallback, useEffect, useMemo, useState} from 'react';

import Slider from '@/components/common/slider';
import ProductViewToggle from '@/components/productViewToggle';
import StatsGridComponent from '@/components/statsGridComponent';

import {useCompetitiveAnalysis} from '../hooks/useCompetitiveAnalysis';
import {useListingSearch} from '../hooks/useListingSearch';
import {
  CompetitiveAnalysisPropsT,
  CompetitiveAnalysisSortOption,
  ListingSearchResultT,
  ProductPriceComparisonT,
} from '../types/competitive-analysis.types';
import {CompetitorMap} from './CompetitorMap';

const sortOptions: CompetitiveAnalysisSortOption[] = [
  {label: 'Product Name (A-Z)', field: 'productName', direction: 'asc'},
  {label: 'Product Name (Z-A)', field: 'productName', direction: 'desc'},
  {label: 'Your Price (Low to High)', field: 'currentPrice', direction: 'asc'},
  {label: 'Your Price (High to Low)', field: 'currentPrice', direction: 'desc'},
  {label: 'Price Difference (Low to High)', field: 'priceDifference', direction: 'asc'},
  {label: 'Price Difference (High to Low)', field: 'priceDifference', direction: 'desc'},
  {label: 'Competitor Count (Low to High)', field: 'competitorCount', direction: 'asc'},
  {label: 'Competitor Count (High to Low)', field: 'competitorCount', direction: 'desc'},
];

export const CompetitiveAnalysis: FC<CompetitiveAnalysisPropsT> = () => {
  const [expandedProductId, setExpandedProductId] = useState<string | null>(null);
  const [localSearchRadius, setLocalSearchRadius] = useState(5);
  const [localProductSearch, setLocalProductSearch] = useState('');
  const [localBrandSearch, setLocalBrandSearch] = useState('');

  const {setSearchTerm, results: searchResults, isLoading: isSearchLoading} = useListingSearch();

  const {
    selectedListing,
    searchRadius,
    setSelectedListing,
    setSearchRadius,
    isLoading: isAnalysisLoading,
    error,
    sortOption,
    setSortOption,
    filterState,
    setFilterState,
    displayedProducts,
    competitorLocations,
    productViewMode,
    setProductViewMode,
    pagination,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
  } = useCompetitiveAnalysis();

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [
    searchRadius,
    filterState.searchInput,
    filterState.brandSearchInput,
    sortOption,
    selectedListing,
    setCurrentPage,
  ]);

  // Initialize local states from filter state
  useEffect(() => {
    setLocalProductSearch(filterState.searchInput);
    setLocalBrandSearch(filterState.brandSearchInput);
    setLocalSearchRadius(searchRadius);
  }, [filterState.searchInput, filterState.brandSearchInput, searchRadius]);

  // Debounced search handlers
  const debouncedSetFilterState = useMemo(
    () =>
      debounce((newState: typeof filterState) => {
        setFilterState(newState);
      }, 500),
    [setFilterState]
  );

  // Debounced radius handler
  const debouncedSetSearchRadius = useMemo(
    () =>
      debounce((radius: number) => {
        setSearchRadius(radius);
      }, 500),
    [setSearchRadius]
  );

  // Cleanup debounced functions
  useEffect(() => {
    return () => {
      debouncedSetFilterState.cancel();
      debouncedSetSearchRadius.cancel();
    };
  }, [debouncedSetFilterState, debouncedSetSearchRadius]);

  const handleProductSearch = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setLocalProductSearch(value);
      debouncedSetFilterState({...filterState, searchInput: value});
    },
    [filterState, debouncedSetFilterState]
  );

  const handleBrandSearch = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setLocalBrandSearch(value);
      debouncedSetFilterState({...filterState, brandSearchInput: value});
    },
    [filterState, debouncedSetFilterState]
  );

  const handleRadiusChange = useCallback((value: number) => {
    setLocalSearchRadius(value);
  }, []);

  const handleRadiusChangeEnd = useCallback(
    (value: number) => {
      debouncedSetSearchRadius(value);
    },
    [debouncedSetSearchRadius]
  );

  const handlePageChange = useCallback(
    (page: number) => {
      setCurrentPage(page);
    },
    [setCurrentPage]
  );

  const handleItemsPerPageChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const newLimit = parseInt(event.target.value, 10);
      setItemsPerPage(newLimit);
      setCurrentPage(1); // Reset to first page when changing items per page
    },
    [setItemsPerPage, setCurrentPage]
  );

  if (error) {
    return <div className="text-red-500 p-4">An error occurred while fetching data. Please try again later.</div>;
  }

  return (
    <div className="box space-y-6">
      {selectedListing && (
        <>
          {/* Map Section */}
          <div className="rounded-lg p-6 pb-0 shadow-sm">
            <h2 className="mb-4 text-xl font-semibold">Competitor Map</h2>
            <CompetitorMap selectedListing={selectedListing} competitors={competitorLocations} />
          </div>

          {/* Analytics Stats */}
          <div className="mt-4 rounded-lg p-6 shadow-sm">
            <StatsGridComponent
              stats={[
                {
                  title: 'Competitors',
                  value: competitorLocations.length,
                  // count of competitors isDelivery or isPickup
                  subtitle: `${competitorLocations.filter(c => c.isDelivery).length} DELIVERY, ${competitorLocations.filter(c => !c.isDelivery).length} STOREFRONT`,
                  subValue: `${searchRadius}mi`,
                  cardClass: 'bg-primary',
                  icon: 'ri-team-line',
                  color: 'primary',
                  subTextClass: 'text-white',
                },
                {
                  title: 'competing products',
                  value: displayedProducts.filter(
                    (p: ProductPriceComparisonT) => p.competitorPrices.competitors.length > 0
                  ).length,
                  subtitle: `${productViewMode === 'verified' ? 'Verified' : productViewMode === 'unverified' ? 'Unverified' : 'All'} Products`,
                  cardClass: 'bg-primary',
                  icon: 'ri-check-double-line',
                  color: 'primary',
                  subTextClass: 'text-white',
                },
                {
                  title: 'non-competing products',
                  value: displayedProducts.filter(
                    (p: ProductPriceComparisonT) => p.competitorPrices.competitors.length === 0
                  ).length,
                  subtitle: `${productViewMode === 'verified' ? 'Verified' : productViewMode === 'unverified' ? 'Unverified' : 'All'} Products`,
                  cardClass: 'bg-primary',
                  icon: 'ri-error-warning-line',
                  color: 'primary',
                  subTextClass: 'text-white',
                },
              ]}
              columns={3}
            />
          </div>
        </>
      )}

      {/* Price Comparison Section */}
      <div className={clsx('mt-0 rounded-lg p-6 pt-0 shadow-sm', !selectedListing && 'pt-6')}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold">Price Comparison</h2>
          <ProductViewToggle viewMode={productViewMode} onChange={setProductViewMode} />
        </div>
        <div className="mb-6 flex flex-wrap gap-4">
          {/* Listing Search */}
          <div className="min-w-[300px] flex-1">
            <Combobox value={selectedListing} onChange={setSelectedListing}>
              <div className="relative">
                <ComboboxInput
                  className="w-full rounded-md border bg-black px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Search for a retailer..."
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                  displayValue={(listing: ListingSearchResultT | null) => listing?.name || ''}
                />
                <ComboboxOptions className="bordershadow-lg absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-black">
                  {isSearchLoading ? (
                    <div className="p-4 text-gray-500">Loading...</div>
                  ) : searchResults.length === 0 ? (
                    <div className="p-4 text-gray-500">No retailers found</div>
                  ) : (
                    searchResults.map((listing, index) => (
                      <ComboboxOption
                        key={`${listing.id}-${index}`}
                        value={listing}
                        className={({active}: {active: boolean}) =>
                          `cursor-pointer p-4 ${active ? 'bg-primary text-white' : 'text-white'}`
                        }
                      >
                        {listing.name}
                      </ComboboxOption>
                    ))
                  )}
                </ComboboxOptions>
              </div>
            </Combobox>
          </div>

          {/* Product Search */}
          <div className="min-w-[300px] flex-1">
            <input
              type="text"
              className="w-full rounded-md border bg-black px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Search products..."
              value={localProductSearch}
              onChange={handleProductSearch}
            />
          </div>

          {/* Brand Search */}
          <div className="min-w-[300px] flex-1">
            <input
              type="text"
              className="w-full rounded-md border bg-black px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Search brands..."
              value={localBrandSearch}
              onChange={handleBrandSearch}
            />
          </div>
          {/* Sort Dropdown */}
          <div className="min-w-[300px] flex-1">
            <select
              className="w-full rounded-md border bg-black px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
              value={`${sortOption.field}-${sortOption.direction}`}
              onChange={e => {
                const option = sortOptions.find(opt => `${opt.field}-${opt.direction}` === e.target.value);
                if (option) {
                  setSortOption(option);
                }
              }}
            >
              {sortOptions.map((option, index) => (
                <option
                  key={`${option.field}-${option.direction}-${index}`}
                  value={`${option.field}-${option.direction}`}
                >
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          {/* Radius Slider */}
          <div className="min-w-[300px] flex-1">
            <label className="mb-2 block text-sm font-medium text-gray-700">
              Search Radius: {localSearchRadius} miles
            </label>
            <Slider
              min={5}
              max={50}
              steps={5}
              value={localSearchRadius}
              onChange={handleRadiusChange}
              onChangeEnd={handleRadiusChangeEnd}
              className="w-full"
            />
          </div>
        </div>

        {/* Pagination Controls */}
        <div className="flex items-center justify-between border-t border-gray-700 bg-black px-4 py-3 sm:px-6">
          <div className="flex flex-1 justify-between sm:hidden">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="relative inline-flex items-center rounded-md border border-gray-700 bg-black px-4 py-2 text-sm font-medium text-gray-400 hover:bg-gray-800"
            >
              Previous
            </button>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === pagination.totalPages}
              className="relative ml-3 inline-flex items-center rounded-md border border-gray-700 bg-black px-4 py-2 text-sm font-medium text-gray-400 hover:bg-gray-800"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-400">
                Showing <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> to{' '}
                <span className="font-medium">{Math.min(currentPage * itemsPerPage, pagination.totalItems)}</span> of{' '}
                <span className="font-medium">{pagination.totalItems}</span> results
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <select
                value={itemsPerPage}
                onChange={handleItemsPerPageChange}
                className="rounded-md border border-gray-700 bg-black px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {[10, 25, 50, 100].map(size => (
                  <option key={size} value={size}>
                    {size} per page
                  </option>
                ))}
              </select>
              <nav className="relative z-0 inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center rounded-l-md border border-gray-700 bg-black px-2 py-2 text-sm font-medium text-gray-400 hover:bg-gray-800"
                >
                  <span className="sr-only">Previous</span>
                  <i className="ri-arrow-left-s-line" />
                </button>
                {/* Page Numbers */}
                {Array.from({length: Math.min(5, pagination.totalPages)}, (_, i) => {
                  const pageNum = i + 1;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={clsx(
                        'relative inline-flex items-center border border-gray-700 px-4 py-2 text-sm font-medium',
                        currentPage === pageNum ? 'bg-primary text-white' : 'bg-black text-gray-400 hover:bg-gray-800'
                      )}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === pagination.totalPages}
                  className="relative inline-flex items-center rounded-r-md border border-gray-700 bg-black px-2 py-2 text-sm font-medium text-gray-400 hover:bg-gray-800"
                >
                  <span className="sr-only">Next</span>
                  <i className="ri-arrow-right-s-line" />
                </button>
              </nav>
            </div>
          </div>
        </div>

        {/* Results Table */}
        {!selectedListing ? (
          <div className="p-4 text-center text-gray-500">Select a retailer to view competitive analysis</div>
        ) : displayedProducts.length === 0 && !isAnalysisLoading ? (
          <div className="p-4 text-center text-gray-500">No products found for competitive analysis</div>
        ) : (
          <div className="relative overflow-x-auto">
            {/* Loading Overlay */}
            {isAnalysisLoading && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-black bg-opacity-50">
                <div className="flex flex-col items-center gap-4">
                  <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
                  <div className="text-sm text-white">Loading analysis...</div>
                </div>
              </div>
            )}

            <table className="table-hover table-bordered table-sm ti-striped-table table min-w-full whitespace-nowrap">
              <thead>
                <tr>
                  <th className="text-start">Brand</th>
                  <th className="text-start">Product</th>
                  <th className="text-end">Original Price</th>
                  <th className="text-end">Adjusted Price</th>
                  <th className="text-end">Min Price</th>
                  <th className="text-end">Max Price</th>
                  <th className="text-end">Avg Price</th>
                  <th className="text-center">Price Difference</th>
                  <th className="text-center">Competitors</th>
                </tr>
              </thead>
              <tbody>
                {displayedProducts?.map((product: ProductPriceComparisonT, index) => {
                  const priceDiffFromAverage =
                    ((product.currentPrice - product.competitorPrices.avg) / product.competitorPrices.avg) * 100;
                  const priceDiffFromLowest =
                    ((product.currentPrice - product.competitorPrices.min) / product.competitorPrices.min) * 100;
                  const uniqueKey = `${product.productId}-${product.productName}-${
                    product.brand.brandName === 'Unverified' ? 'unverified' : 'verified'
                  }-${index}`;
                  const isExpanded = expandedProductId === uniqueKey;

                  return (
                    <React.Fragment key={uniqueKey}>
                      <tr
                        className={clsx(
                          'border border-solid border-inherit hover:bg-gray-100 dark:border-defaultborder/10 dark:hover:bg-light',
                          {
                            'bg-gray-800': isExpanded,
                          }
                        )}
                        onClick={() => setExpandedProductId(isExpanded ? null : uniqueKey)}
                      >
                        <td className="p-4">
                          <div className="flex items-start gap-2">
                            <img
                              src={product.brand.brandLogo || '/images/logo/logo.png'}
                              alt={product.brand.brandName}
                              className="h-10 w-10 shrink-0 rounded-sm bg-white object-contain"
                            />
                            <span className="whitespace-normal break-words text-sm font-medium">
                              {product.brand.brandName}
                            </span>
                          </div>
                        </td>
                        <td className="max-w-sm p-4">
                          <div className="flex items-start gap-2">
                            <img
                              src={product.productLogo || '/images/logo/logo.png'}
                              alt={product.productName}
                              className="h-10 w-10 shrink-0 rounded-sm bg-white object-contain"
                            />
                            <span className="whitespace-normal break-words text-sm font-medium">
                              {product.productName}
                            </span>
                          </div>
                        </td>
                        <td className={clsx('max-w-xs overflow-hidden whitespace-nowrap text-right')}>
                          <div className="flex items-center justify-end gap-2">
                            <span>${product.originalPrice.toFixed(2)}</span>
                          </div>
                        </td>
                        <td
                          className={clsx(
                            'max-w-xs overflow-hidden whitespace-nowrap text-right',
                            product.currentPrice > product.competitorPrices.min ? 'text-red' : 'text-green'
                          )}
                        >
                          <div className="flex items-center justify-end gap-2">
                            {product.discountLabel && (
                              <div className="rounded-full bg-primary px-2 py-1 text-xs font-medium text-white">
                                {product.discountLabel}
                              </div>
                            )}
                            <span>${product.currentPrice.toFixed(2)}</span>
                          </div>
                        </td>
                        <td
                          className={clsx(
                            'max-w-xs overflow-hidden whitespace-nowrap text-right',
                            product.currentPrice < product.competitorPrices.min ? 'text-red' : 'text-green'
                          )}
                        >
                          ${product.competitorPrices.min.toFixed(2)}
                        </td>
                        <td className="max-w-xs overflow-hidden whitespace-nowrap text-right">
                          ${product.competitorPrices.max.toFixed(2)}
                        </td>
                        <td className="max-w-xs overflow-hidden whitespace-nowrap text-right">
                          ${product.competitorPrices.avg.toFixed(2)}
                        </td>
                        <td className="max-w-xs space-x-2 overflow-hidden whitespace-nowrap text-center">
                          {product.competitorPrices.avg > 0 && (
                            <span
                              className={clsx(
                                'badge inline-flex items-center whitespace-nowrap px-3 py-1 text-xs font-medium',
                                priceDiffFromAverage > 0
                                  ? 'bg-danger'
                                  : priceDiffFromAverage < 0
                                    ? 'bg-success'
                                    : 'bg-secondary'
                              )}
                            >
                              {priceDiffFromAverage > 0 ? '↑' : priceDiffFromAverage < 0 ? '↓' : ''}
                              {Math.abs(priceDiffFromAverage).toFixed(1)}%
                              <span className="ml-1 text-[10px]">
                                {priceDiffFromAverage > 0 ? 'avg' : priceDiffFromAverage < 0 ? 'avg' : 'avg'}
                              </span>
                            </span>
                          )}
                          {product.competitorPrices.min > 0 && (
                            <span
                              className={clsx(
                                'badge inline-flex items-center whitespace-nowrap px-3 py-1 text-xs font-medium',
                                priceDiffFromLowest > 0
                                  ? 'bg-danger'
                                  : priceDiffFromLowest < 0
                                    ? 'bg-success'
                                    : 'bg-secondary'
                              )}
                            >
                              {priceDiffFromLowest > 0 ? '↑' : priceDiffFromLowest < 0 ? '↓' : ''}
                              {Math.abs(priceDiffFromLowest).toFixed(1)}%
                              <span className="ml-1 text-[10px]">
                                {priceDiffFromLowest > 0 ? 'min' : priceDiffFromLowest < 0 ? 'min' : 'min'}
                              </span>
                            </span>
                          )}
                        </td>
                        <td className="max-w-xs overflow-hidden whitespace-nowrap text-center">
                          <span className="inline-flex items-center justify-center rounded-full bg-gray-700 px-3 py-1 text-sm font-medium text-white">
                            {product.competitorPrices.competitors.length}
                          </span>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="bg-gray-800">
                          <td colSpan={8} className="p-4">
                            <div className="rounded-md bg-black p-4">
                              <h4 className="mb-4 text-sm font-medium">Competitor Details</h4>
                              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                                {[...product.competitorPrices.competitors]
                                  .sort((a, b) => a.price - b.price)
                                  .map((competitor, index) => (
                                    <div
                                      key={`${competitor.listingId}-${index}`}
                                      className="relative flex min-h-[64px] items-start gap-2 rounded-md bg-gray-800 p-3"
                                    >
                                      <div className="min-w-0 flex-1">
                                        <span className="block overflow-hidden text-ellipsis break-words text-sm font-medium">
                                          {competitor.listingName}
                                        </span>
                                        <span className="block overflow-hidden text-ellipsis break-words text-xs text-gray-400">
                                          {competitor.listingAddress}
                                        </span>
                                      </div>
                                      <div className="flex shrink-0 flex-col items-end justify-start gap-1 pl-2">
                                        {competitor.discountLabel && (
                                          <span className="whitespace-nowrap rounded-full bg-primary px-2 py-0.5 text-xs font-medium text-white">
                                            {competitor.discountLabel}
                                          </span>
                                        )}
                                        <span
                                          className={clsx('whitespace-nowrap text-sm font-medium', {
                                            'text-green': competitor.price < product.currentPrice,
                                            'text-gray-400': competitor.price >= product.currentPrice,
                                          })}
                                        >
                                          ${competitor.price.toFixed(2)}
                                        </span>
                                      </div>
                                    </div>
                                  ))}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
